import type { StateCreator } from 'zustand';
import type { Store, CurrentUser, GraphComment } from '../types';

export function createUiActions(
  set: Parameters<StateCreator<Store>>[0],
  get: Parameters<StateCreator<Store>>[1],
) {
  return {
    setReadOnly(isReadOnly: boolean): void {
      set((state) => ({ ui: { ...state.ui, isReadOnly } }));
    },
    setLoading(isLoading: boolean): void {
      set((state) => ({ ui: { ...state.ui, isLoading } }));
    },
    setCurrentUser(currentUser: CurrentUser | null): void {
      set((state) => ({ ui: { ...state.ui, currentUser } }));
    },
    setVersionId(versionId: string | null): void {
      set((state) => ({ ui: { ...state.ui, versionId } }));
    },
    addGraphComment(comment: GraphComment): void {
      set((state) => ({
        graphData: {
          ...state.graphData,
          graphComments: [...state.graphData.graphComments, comment],
        },
      }));
    },
    setGraphComments(comments: GraphComment[]): void {
      set((state) => ({
        graphData: {
          ...state.graphData,
          graphComments: comments,
        },
      }));
    },
    removeGraphComment(commentId: string): void {
      set((state) => ({
        graphData: {
          ...state.graphData,
          graphComments: state.graphData.graphComments.filter((c) => c.id !== commentId && c._id !== commentId),
        },
      }));
    },
    clearDirty(): void {
      set((state) => ({ ui: { ...state.ui, isDirty: false } }));
    },
    setLockState(lockState: Partial<{
      isProtocolLocked: boolean;
      isLifecycleLocked: boolean;
      isArchived: boolean;
      isArchivalInProgress: boolean;
      runsExist: boolean;
    }>): void {
      set((state) => {
        const newUi = { ...state.ui, ...lockState };
        
        // Recalculate lock reason based on new state
        let lockReason: string | null = null;
        if (newUi.isArchived) {
          lockReason = 'The file cannot be modified as it is archived.';
        } else if (newUi.isArchivalInProgress) {
          lockReason = 'The file is under archival. Please complete the archival flow.';
        } else if (newUi.isProtocolLocked) {
          lockReason = 'This file is protocol-locked and cannot be modified.';
        } else if (newUi.isLifecycleLocked) {
          lockReason = 'This file is locked by lifecycle rules. Save as new version to edit.';
        } else if (newUi.runsExist) {
          lockReason = 'Cannot unlock: Runs already exist for this version.';
        }
        
        return { ui: { ...newUi, lockReason } };
      });
    },
    getCanToggleLock(): boolean {
      const state = get();
      const result = (
        !state.ui.isArchived &&
        !state.ui.isArchivalInProgress &&
        !state.ui.runsExist
      );
      return result;
    },
  };
}
